import { useNavigate } from "react-router-dom";
import Tooltip from "../../../../Components/Tooltip";
import { FaPlusCircle } from "react-icons/fa";
import ProgressBarCard from "../ProgressBarCard";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { useState, useEffect } from "react";
import useAuthStore from "../../../../store/useAuthStore";

function PayableCard() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  
  // State for dynamic payable data
  const [payableData, setPayableData] = useState({
    dueToday: 0,
    dueWithin30Days: 0,
    totalOutstanding: 0,
  });
  const [loading, setLoading] = useState(true);

  // Fetch payable data from backend
  useEffect(() => {
    const fetchPayableData = async () => {
      setLoading(true);
      try {
        const response = await fetch("/api/expenseSummary", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        
        // Handle potential different field names
        const dueWithin30Days = data.dueWithin30Days || data.dueThisMonth || data.due_within_30_days || data.due_this_month || 0;
        
        setPayableData({
          dueToday: data.dueToday || data.due_today || 0,
          dueWithin30Days: dueWithin30Days,
          totalOutstanding: data.totalOutstanding || data.total_outstanding || data.totalPayables || data.total_payables || 0,
        });
      } catch (error) {
        console.error("Error fetching payable data:", error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPayableData();
    }
  }, [token]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="animate-pulse bg-gray-200 rounded-md h-4 w-16"></div>
  );

  return (
    <div className="bg-white flex-1 rounded-lg shadow-md border border-gray-200">
      <div className="flex items-center w-full p-4 border-b bg-white rounded-t-lg relative gap-x-1 md:gap-x-2 md:p-4 z-10">
        <h4 className="text-base md:text-lg font-medium text-gray-700">
          Total Payable
        </h4>
        <Tooltip
          tooltip="Current and overdue amount that you're yet to pay to vendors"
          position="bottom"
        >
          <IoMdInformationCircleOutline className="text-gray-500 text-xl cursor-pointer" />
        </Tooltip>
      </div>

      <div className="flex items-center py-2">
        <div className="py-2 pr:4 md:pl-10 md:pr-4">
          <ProgressBarCard
            current={payableData.totalOutstanding}
            overdue={payableData.dueWithin30Days}
            cardType="payable"
          />
        </div>

        <div className="border-l border-gray-300 mx-6 h-[120px] hidden sm:block"></div>

        <div className="flex flex-col gap-y-4 justify-center w-full px-4">
          <button
            onClick={() => handleNavigation("/NewExpense")}
            className="flex items-center gap-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 shadow-md w-[130px] ml-auto"
          >
            <FaPlusCircle className="text-white" />
            <p className="text-sm"> Expense</p>
          </button>
          
          <div className="grid grid-cols-[60%_40%] gap-x-4 mt-2">
            <div className="text-sm text-gray-600 text-left">
              Due Today
            </div>
            <div className="text-sm text-gray-600 text-left break-words">
              {loading ? <LoadingSkeleton /> : `Nu. ${payableData.dueToday.toLocaleString()}`}
            </div>

            <div className="text-sm text-gray-600 text-left">
              Due This Month
            </div>
            <div className="text-sm text-gray-600 text-left break-words">
              {loading ? <LoadingSkeleton /> : `Nu. ${payableData.dueWithin30Days.toLocaleString()}`}
            </div>

            <div className="text-sm text-gray-600 text-left">
              Total Payables
            </div>
            <div className="text-sm text-gray-600 text-left break-words">
              {loading ? <LoadingSkeleton /> : `Nu. ${payableData.totalOutstanding.toLocaleString()}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PayableCard;